import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPaperPlane, FaBell, FaCheckCircle, FaHistory } from "react-icons/fa";
import { notificationSender } from "../api/apiServices";

export default function NotificationsPage() {
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("all"); 
  const [sending, setSending] = useState(false);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Readable labels for the UI
  const getTargetLabel = (val) => {
    const options = {
      rider: "Riders",
      vendor: "Vendors",
      user: "Customers",
    };
    return options[val] || val;
  };

  useEffect(() => {
    // In a real app, you might fetch history from the DB here
    setSentNotifications([
      {
        id: 1,
        title: "System Maintenance",
        message: "The platform will undergo maintenance from 2AM to 4AM.",
        target: "all",
        timestamp: "2 hours ago",
        sentTo: "All Users",
      },
    ]);
  }, []);

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;

    setSending(true);
    setError(null);

    // This payload matches your backend broadcastNotification req.body
    const payload = {
      title: title.trim(),
      message: message.trim(),
      target: target,
      extraData: {
        sentAt: new Date().toISOString(),
        category: target
      }
    };

    try {
      // ACTUAL API CALL
      await notificationSender(payload);

      const newNotif = {
        id: Date.now(),
        title: title.trim(),
        message: message.trim(),
        target,
        timestamp: "Just now",
        sentTo: getTargetLabel(target),
      };

      setSentNotifications((prev) => [newNotif, ...prev]);
      setSuccess(true);
      
      // Reset Form
      setTitle("");
      setMessage("");
      setTarget("all");

      // Auto-hide success message
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to broadcast notification.");
      console.error("Broadcast Error:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 pt-8 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-orange-100 rounded-lg text-orange-600">
              <FaBell size={24} />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Push Notifications</h1>
          </div>
          <p className="text-gray-500">
            Broadcast instant updates to your platform members via WebSockets and FCM.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-8">
        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8 mb-8"
        >
          <form onSubmit={handleSendNotification} className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Flash Sale Alert!"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notification Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none transition-all resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Receiver Category
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: "rider", label: "Riders" },
                    { value: "vendor", label: "Vendors" },
                    { value: "user", label: "Customers" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setTarget(option.value)}
                      className={`py-3 px-4 rounded-xl border text-sm font-medium transition-all ${
                        target === option.value
                          ? "border-orange-500 bg-orange-50 text-orange-600 shadow-sm"
                          : "border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={sending || !title.trim() || !message.trim()}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Broadcasting...
                  </span>
                ) : (
                  <>
                    <FaPaperPlane /> Send Notification
                  </>
                )}
              </button>
            </div>
          </form>

          <AnimatePresence>
            {success && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 flex items-center gap-3 text-green-700 bg-green-50 p-4 rounded-xl border border-green-100"
              >
                <FaCheckCircle />
                <p className="text-sm font-medium">
                  Success! Notification sent to {getTargetLabel(target)}.
                </p>
              </motion.div>
            )}
            
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 text-red-700 bg-red-50 p-4 rounded-xl border border-red-100 text-sm font-medium"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* History Section */}
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-6 text-gray-800">
            <FaHistory className="text-gray-400" />
            <h2 className="text-xl font-bold">History</h2>
          </div>

          <div className="space-y-4">
            {sentNotifications.map((notif) => (
              <div
                key={notif.id}
                className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex flex-col md:flex-row justify-between gap-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-bold text-gray-800">{notif.title}</h3>
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-orange-100 text-orange-600 rounded">
                      {notif.target}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{notif.message}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-gray-400 font-medium">{notif.timestamp}</p>
                </div>
              </div>
            ))}

            {sentNotifications.length === 0 && (
              <div className="text-center py-20 bg-gray-100/50 rounded-2xl border-2 border-dashed border-gray-200">
                <p className="text-gray-400">No recent notifications</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}